import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Chip, InputBase, Stack, Typography } from '@mui/material';
import { livyJson } from 'src/lib/livyApi';
import { LivyEventLine, LivyRepLine } from 'src/components/livy/LivyLive';
import { LivyMarkdown } from 'src/components/livy/LivyMarkdown';
import { withoutHandoff } from 'src/components/livy/LivySupportCard';
import { summariseTools } from 'src/components/livy/toolLabels';
import { splitSuggestions } from 'src/components/livy/suggestions';
/** The queue refreshes this often, and opening it is what tells the chat a rep is online. */
const QUEUE_POLL_MS = 5000;
const CHAT_POLL_MS = 3000;
const STATE_CHIP = {
  waiting: {
    label: 'Waiting',
    color: 'warning'
  },
  joined: {
    label: 'In chat',
    color: 'success'
  },
  missed: {
    label: 'Missed, went to email',
    color: 'error'
  },
  ended: {
    label: 'Ended',
    color: 'default'
  }
};
const minutesSince = iso => Math.max(0, Math.round((Date.now() - Date.parse(iso)) / 60000));

/**
 * Live handoff, the rep's side. A customer who asked for a person waits here; Join puts the rep in the chat (Livy
 * goes quiet), what the rep sends shows to the customer under their first name, and End hands back to Livy.
 * Every route behind this is staff only in the proxy (/live is ACCESS.ADMIN); hiding the tab is not the gate.
 */
export function LiveTab() {
  var _chat$tools;
  const _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    queue = _useState2[0],
    setQueue = _useState2[1];
  const _useState3 = useState(() => new URLSearchParams(window.location.search).get('c')),
    _useState4 = _slicedToArray(_useState3, 2),
    selected = _useState4[0],
    setSelected = _useState4[1];
  const _useState5 = useState(null),
    _useState6 = _slicedToArray(_useState5, 2),
    chat = _useState6[0],
    setChat = _useState6[1];
  const _useState7 = useState(''),
    _useState8 = _slicedToArray(_useState7, 2),
    draft = _useState8[0],
    setDraft = _useState8[1];
  const _useState9 = useState(false),
    _useState0 = _slicedToArray(_useState9, 2),
    busy = _useState0[0],
    setBusy = _useState0[1];
  const _useState1 = useState(null),
    _useState10 = _slicedToArray(_useState1, 2),
    error = _useState10[0],
    setError = _useState10[1];
  const _useState11 = useState(null),
    _useState12 = _slicedToArray(_useState11, 2),
    pollError = _useState12[0],
    setPollError = _useState12[1];
  const loadQueue = useCallback(() => {
    livyJson('/live').then(d => {
      setQueue(d.live);
      setPollError(null);
    }).catch(e => setPollError(e.message));
  }, []);
  useEffect(() => {
    loadQueue();
    const t = setInterval(loadQueue, QUEUE_POLL_MS);
    return () => clearInterval(t);
  }, [loadQueue]);

  // A response for a chat the rep has since left is dropped, so what is on screen is always the chat Join, Send and
  // End act on.
  const selectedRef = useRef(selected);
  selectedRef.current = selected;
  const loadChat = useCallback(() => {
    const asked = selected;
    if (!asked) return;
    livyJson("/live/".concat(encodeURIComponent(asked))).then(d => {
      var _d$live;
      if (selectedRef.current === asked && ((_d$live = d.live) === null || _d$live === void 0 ? void 0 : _d$live.conversationId) === asked) setChat(d);
    }).catch(e => {
      if (selectedRef.current === asked) setPollError(e.message);
    });
  }, [selected]);
  useEffect(() => {
    setChat(null);
    loadChat();
    const t = setInterval(loadChat, CHAT_POLL_MS);
    return () => clearInterval(t);
  }, [loadChat]);
  const createActionHandler = action => async () => {
    if (!selected || busy || (chat === null || chat === void 0 ? void 0 : chat.live.conversationId) !== selected) return;
    if (action === 'send' && !draft.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await livyJson("/live/".concat(encodeURIComponent(selected)), {
        method: 'POST',
        body: action === 'send' ? {
          action,
          text: draft
        } : {
          action
        }
      });
      if (action === 'send') setDraft('');
      loadChat();
      loadQueue();
    } catch (e) {
      const message = e.message;
      setError(message === 'live_state_changed' ? 'The chat changed state. It has been refreshed.' : message);
      loadChat();
    } finally {
      setBusy(false);
    }
  };
  const handleDraftKeyDown = e => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void createActionHandler('send')();
    }
  };

  // Actions only on the chat that is on screen.
  const state = (chat === null || chat === void 0 ? void 0 : chat.live.conversationId) === selected ? chat === null || chat === void 0 ? void 0 : chat.live.state : undefined;
  const used = summariseTools((_chat$tools = chat === null || chat === void 0 ? void 0 : chat.tools) !== null && _chat$tools !== void 0 ? _chat$tools : []);
  return /*#__PURE__*/React.createElement(Stack, {
    direction: {
      xs: 'column',
      md: 'row'
    },
    spacing: "20px",
    sx: {
      alignItems: 'stretch',
      minHeight: 480
    }
  }, /*#__PURE__*/React.createElement(Box, {
    sx: {
      width: {
        md: 320
      },
      flexShrink: 0
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    sx: {
      fontWeight: 600,
      mb: '4px'
    }
  }, "Customers asking for a person"), /*#__PURE__*/React.createElement(Typography, {
    sx: {
      fontSize: 12.5,
      color: 'text.secondary',
      mb: '12px'
    }
  }, "While this tab is open, customers see \u201CTalk to a person now\u201D. Nobody joining in 5 minutes sends them to email; the ticket is already filed."), queue === null && !error && /*#__PURE__*/React.createElement(Typography, {
    sx: {
      fontSize: 13
    }
  }, "Loading\u2026"), (queue === null || queue === void 0 ? void 0 : queue.length) === 0 && /*#__PURE__*/React.createElement(Typography, {
    sx: {
      fontSize: 13,
      color: 'text.secondary'
    }
  }, "Nobody is waiting."), /*#__PURE__*/React.createElement(Stack, {
    spacing: "8px"
  }, queue === null || queue === void 0 ? void 0 : queue.map(r => /*#__PURE__*/React.createElement(Box, {
    key: r.conversationId,
    component: "button",
    type: "button",
    onClick: () => setSelected(r.conversationId),
    sx: {
      textAlign: 'left',
      p: '10px 12px',
      borderRadius: '8px',
      border: 1,
      borderColor: selected === r.conversationId ? 'primary.main' : 'divider',
      bgcolor: 'background.paper',
      color: 'text.primary',
      cursor: 'pointer',
      font: 'inherit',
      '&:focus-visible': {
        outline: '2px solid',
        outlineColor: 'primary.main'
      }
    }
  }, /*#__PURE__*/React.createElement(Stack, {
    direction: "row",
    spacing: "6px",
    sx: {
      alignItems: 'center',
      mb: '4px'
    }
  }, /*#__PURE__*/React.createElement(Chip, {
    size: "small",
    label: STATE_CHIP[r.state].label,
    color: STATE_CHIP[r.state].color
  }), r.test && /*#__PURE__*/React.createElement(Chip, {
    size: "small",
    label: "Staff test",
    variant: "outlined"
  }), /*#__PURE__*/React.createElement(Typography, {
    sx: {
      fontSize: 12,
      color: 'text.secondary'
    }
  }, minutesSince(r.requestedAt), " min")), /*#__PURE__*/React.createElement(Typography, {
    sx: {
      fontSize: 13,
      display: '-webkit-box',
      WebkitLineClamp: 2,
      WebkitBoxOrient: 'vertical',
      overflow: 'hidden'
    }
  }, r.summary))))), /*#__PURE__*/React.createElement(Box, {
    sx: {
      flex: 1,
      minWidth: 0,
      border: 1,
      borderColor: 'divider',
      borderRadius: '10px',
      p: '16px',
      display: 'flex',
      flexDirection: 'column'
    }
  }, error && /*#__PURE__*/React.createElement(Alert, {
    severity: "warning",
    onClose: () => setError(null),
    sx: {
      mb: '12px'
    }
  }, error), pollError && !error && /*#__PURE__*/React.createElement(Typography, {
    sx: {
      fontSize: 12.5,
      color: 'text.secondary',
      mb: '8px'
    }
  }, "Refreshing failed (", pollError, "). Retrying."), !selected && /*#__PURE__*/React.createElement(Typography, {
    sx: {
      color: 'text.secondary'
    }
  }, "Pick a chat on the left."), selected && !chat && /*#__PURE__*/React.createElement(Typography, {
    sx: {
      fontSize: 13
    }
  }, "Loading the chat\u2026"), chat && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Stack, {
    direction: "row",
    spacing: "8px",
    sx: {
      alignItems: 'center',
      mb: '10px',
      flexWrap: 'wrap',
      rowGap: '6px'
    }
  }, /*#__PURE__*/React.createElement(Chip, {
    size: "small",
    label: STATE_CHIP[chat.live.state].label,
    color: STATE_CHIP[chat.live.state].color
  }), chat.live.agentName && state === 'joined' && /*#__PURE__*/React.createElement(Typography, {
    sx: {
      fontSize: 12.5,
      color: 'text.secondary'
    }
  }, chat.live.agentName, " is in the chat"), /*#__PURE__*/React.createElement(Box, {
    sx: {
      flex: 1
    }
  }), (state === 'waiting' || state === 'missed') && /*#__PURE__*/React.createElement(Button, {
    size: "small",
    variant: "contained",
    disabled: busy,
    onClick: createActionHandler('join'),
    sx: {
      textTransform: 'none'
    }
  }, "Join chat"), state === 'joined' && /*#__PURE__*/React.createElement(Button, {
    size: "small",
    disabled: busy,
    onClick: createActionHandler('end'),
    sx: {
      textTransform: 'none'
    }
  }, "End and hand back to Livy")), /*#__PURE__*/React.createElement(Typography, {
    sx: {
      fontSize: 12.5,
      color: 'text.secondary',
      mb: '12px',
      whiteSpace: 'pre-wrap'
    }
  }, chat.live.summary), used.length > 0 && /*#__PURE__*/React.createElement(Typography, {
    sx: {
      fontSize: 12,
      color: 'text.secondary',
      mb: '12px'
    }
  }, "Livy looked at: ", used.map(u => u.label).join(', ')), /*#__PURE__*/React.createElement(Box, {
    sx: {
      flex: 1,
      overflowY: 'auto',
      maxHeight: 520,
      pr: '4px'
    }
  }, chat.messages.map((m, i) => {
    var _m$id, _m$id2, _m$id3;
    return m.role === 'rep' ? /*#__PURE__*/React.createElement(LivyRepLine, {
      key: (_m$id = m.id) !== null && _m$id !== void 0 ? _m$id : "t".concat(i),
      name: m.name,
      content: m.content
    }) : m.role === 'event' ? /*#__PURE__*/React.createElement(LivyEventLine, {
      key: (_m$id2 = m.id) !== null && _m$id2 !== void 0 ? _m$id2 : "t".concat(i),
      content: m.content
    }) : /*#__PURE__*/React.createElement(Box, {
      key: (_m$id3 = m.id) !== null && _m$id3 !== void 0 ? _m$id3 : "t".concat(i),
      sx: {
        mb: '14px'
      }
    }, /*#__PURE__*/React.createElement(Typography, {
      sx: {
        fontSize: 11,
        fontWeight: 600,
        color: 'text.secondary',
        mb: '2px'
      }
    }, m.role === 'user' ? 'Customer' : 'Livy'), /*#__PURE__*/React.createElement(Box, {
      sx: {
        fontSize: 14
      }
    }, m.role === 'user' ? /*#__PURE__*/React.createElement(Typography, {
      sx: {
        fontSize: 14,
        whiteSpace: 'pre-wrap'
      }
    }, m.content) : /*#__PURE__*/React.createElement(LivyMarkdown, {
      content: splitSuggestions(withoutHandoff(m.content)).body
    })));
  })), state === 'joined' && /*#__PURE__*/React.createElement(Stack, {
    direction: "row",
    spacing: "8px",
    sx: {
      mt: '12px',
      alignItems: 'flex-end'
    }
  }, /*#__PURE__*/React.createElement(InputBase, {
    id: "live-reply",
    multiline: true,
    maxRows: 6,
    value: draft,
    onChange: e => setDraft(e.target.value),
    onKeyDown: handleDraftKeyDown,
    placeholder: "Reply to the customer (Enter sends, Shift+Enter for a new line)",
    inputProps: {
      'aria-label': 'Reply to the customer',
      maxLength: 4000
    },
    sx: {
      flex: 1,
      border: 1,
      borderColor: 'divider',
      borderRadius: '8px',
      px: '10px',
      py: '6px',
      fontSize: 14
    }
  }), /*#__PURE__*/React.createElement(Button, {
    variant: "contained",
    disabled: busy || !draft.trim(),
    onClick: createActionHandler('send'),
    sx: {
      textTransform: 'none'
    }
  }, "Send")))));
}